/**
 * Admin operator consoles kernel (D318): the framework-neutral logic behind the Despacho, Disputas, Segurança e
 * risco, Repasses, Campanhas, Relatórios and Notificações screens. Pure helpers derive what an operator may do
 * next from the durable state the server returned — reusing the real kernels' transition tables (`@vello/safety`,
 * `@vello/risk`, `@vello/ride-collaboration`) so the console can never offer an edge the server would refuse —
 * and thin wrappers call the real operator endpoints (every one AAL2 + RBAC-gated + audited server-side). No
 * fabricated data: every queue is empty until the corresponding feed returns.
 */
import {
  type OpsApi,
  type OpsRideRow,
  type FinanceApi,
  type RideCollaborationApi,
  type DisputeView,
  type DisputeListItem,
  type DispatchWaveResult,
  type PayoutRow,
  type PayoutStatus,
  type PayoutDecision,
  type PayoutDecisionInput,
  type WireMoney,
  type ApiError,
  type OperatingMarketView,
} from '@vello/api-client';
import { type Result } from '@vello/core';
import { SAFETY_INCIDENT_STATUSES, canIncidentTransition, type SafetyIncidentStatus } from '@vello/safety';
import { RISK_CASE_STATUSES, canCaseTransition, type RiskCaseStatus } from '@vello/risk';
import { reviewDispute, rejectDispute, type Dispute } from '@vello/ride-collaboration';
import { loadDashboard } from './ops-console.js';
import { type OperatorScope } from './shell.js';

// ---- shared money (wire → display) ------------------------------------------------------------------

/**
 * Render wire money (integer minor units as a string + ISO 4217) for the operator's locale. Display-only: the
 * division by the currency exponent happens on a Number *after* the ledger `bigint` has been rendered as a
 * string, so it never feeds a calculation. Falls back to `<minor> <CUR>` for an unknown currency code.
 */
export function formatWireMoney(m: WireMoney, locale?: string): string {
  try {
    const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: m.currency });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(Number(m.amountMinor) / 10 ** digits);
  } catch {
    return `${m.amountMinor} ${m.currency}`;
  }
}

/** A non-negative integer minor-unit amount typed by an operator, normalized; undefined when malformed. */
export function parseMinorAmount(raw: string): string | undefined {
  const s = raw.trim();
  return /^\d+$/.test(s) ? String(BigInt(s)) : undefined;
}

/** A three-letter uppercase ISO 4217 code (shape only — the server validates the currency itself). */
export function isCurrencyCode(raw: string): boolean {
  return /^[A-Z]{3}$/.test(raw.trim());
}

// ---- Despacho: the live dispatch queue -----------------------------------------------------------------

export type DispatchLane = 'searching' | 'assigned' | 'inProgress' | 'other';

export interface DispatchQueue {
  /** Rides still looking for a driver — the lane a manual dispatch or a new wave acts on. */
  readonly searching: readonly OpsRideRow[];
  /** A driver is attached but the passenger is not yet aboard (assigned / accepted / en route / arrived). */
  readonly assigned: readonly OpsRideRow[];
  /** The trip is under way. */
  readonly inProgress: readonly OpsRideRow[];
  /** Any other non-terminal status the feed reports. */
  readonly other: readonly OpsRideRow[];
  readonly total: number;
}

/** Terminal ride statuses never belong to the dispatch queue. */
export function isTerminalRideStatus(status: string): boolean {
  return /complet|cancel|expired|failed|no_driver|nodriver/i.test(status);
}

export function laneForStatus(status: string): DispatchLane {
  if (/search|request|pending|matching|offer/i.test(status)) return 'searching';
  if (/in_progress|inprogress|started|on_trip|ontrip|picked/i.test(status)) return 'inProgress';
  if (/assign|accept|matched|en_route|enroute|arriv/i.test(status)) return 'assigned';
  return 'other';
}

/** Group the server's active-ride rows into dispatch lanes (terminal rows dropped; order preserved). */
export function buildDispatchQueue(rides: readonly OpsRideRow[]): DispatchQueue {
  const searching: OpsRideRow[] = [];
  const assigned: OpsRideRow[] = [];
  const inProgress: OpsRideRow[] = [];
  const other: OpsRideRow[] = [];
  for (const r of rides) {
    if (isTerminalRideStatus(r.status)) continue;
    const lane = laneForStatus(r.status);
    if (lane === 'searching') searching.push(r);
    else if (lane === 'assigned') assigned.push(r);
    else if (lane === 'inProgress') inProgress.push(r);
    else other.push(r);
  }
  return { searching, assigned, inProgress, other, total: searching.length + assigned.length + inProgress.length + other.length };
}

/** Load the scoped active-ride queue (the durable ops feed) and lane it. The server enforces scope containment. */
export async function loadDispatchQueue(runtime: { readonly ops: OpsApi }, scope: OperatorScope, markets: readonly OperatingMarketView[] = []): Promise<Result<DispatchQueue, ApiError>> {
  // A state is read market by market (`loadDashboard`), never as its whole country.
  const res = await loadDashboard(runtime, scope, markets);
  return res.ok ? { ok: true, value: buildDispatchQueue(res.value.rides) } : res;
}

/**
 * Issue a dispatch wave to the drivers the operator picked (operator `dispatch:override`; server-audited). The
 * server answers an empty candidate set with `noDriver`, so the screen only enables the action with a selection.
 */
export function issueDispatchWave(runtime: { readonly ops: OpsApi }, rideId: string, candidates: readonly string[], waveNumber?: number): Promise<Result<DispatchWaveResult, ApiError>> {
  return runtime.ops.dispatchWave(rideId, { candidates, ...(waveNumber !== undefined ? { waveNumber } : {}) });
}

// ---- Disputas ------------------------------------------------------------------------------------------

export interface DisputeActions {
  readonly review: boolean;
  readonly resolve: boolean;
  readonly reject: boolean;
  /** Resolved or rejected — nothing left for an operator to do. */
  readonly terminal: boolean;
}

/**
 * What the operator may do next on a dispute, derived from the real kernel transitions (never a client-side
 * copy of the state machine): review needs `open`; resolve and reject share the kernel's *actionable*
 * precondition (`open` or `under_review`).
 */
export function disputeActions(view: DisputeView): DisputeActions {
  const dispute: Dispute = { rideId: view.rideId, reason: view.reason, state: view.state, openedAtMs: view.openedAtMs, ...(view.resolvedAtMs !== undefined ? { resolvedAtMs: view.resolvedAtMs } : {}) };
  const review = reviewDispute(dispute).ok;
  const actionable = rejectDispute(dispute, 0).ok;
  return { review, resolve: actionable, reject: actionable, terminal: !actionable };
}

export function loadDisputeQueue(runtime: { readonly rideCollaboration: RideCollaborationApi }, limit = 50): Promise<Result<{ disputes: readonly DisputeListItem[] }, ApiError>> {
  return runtime.rideCollaboration.listDisputes({ limit });
}

// ---- Segurança e risco ---------------------------------------------------------------------------------

/** The statuses an incident may move to from `from` (per the `@vello/safety` legal-edge table); [] for a terminal or unknown status. */
export function incidentNextStatuses(from: string): readonly SafetyIncidentStatus[] {
  if (!(SAFETY_INCIDENT_STATUSES as readonly string[]).includes(from)) return [];
  return SAFETY_INCIDENT_STATUSES.filter((to) => canIncidentTransition(from as SafetyIncidentStatus, to));
}

/** The statuses a risk case may move to from `from` (per the `@vello/risk` review/appeal table); [] when terminal/unknown. */
export function riskCaseNextStatuses(from: string): readonly RiskCaseStatus[] {
  if (!(RISK_CASE_STATUSES as readonly string[]).includes(from)) return [];
  return RISK_CASE_STATUSES.filter((to) => canCaseTransition(from as RiskCaseStatus, to));
}

// ---- Repasses ------------------------------------------------------------------------------------------

export const PAYOUT_DECISIONS: readonly PayoutDecision[] = ['APPROVE', 'REJECT'];

/** Only a REQUESTED payout takes an operator decision; the rest belong to the settlement rail. */
export function payoutIsDecidable(row: PayoutRow): boolean {
  return row.status === 'REQUESTED';
}

export interface PayoutQueueTotal {
  readonly currency: string;
  readonly count: number;
  /** Exact sum in minor units (bigint arithmetic rendered as a string — never a lossy Number). */
  readonly totalMinor: string;
}

/** Per-currency exact totals of a payout queue (money is never summed across currencies). */
export function summarizePayoutQueue(rows: readonly PayoutRow[]): readonly PayoutQueueTotal[] {
  const acc = new Map<string, { count: number; total: bigint }>();
  for (const r of rows) {
    const cur = acc.get(r.currency) ?? { count: 0, total: 0n };
    let minor = 0n;
    try {
      minor = BigInt(r.amountMinor);
    } catch {
      minor = 0n;
    }
    acc.set(r.currency, { count: cur.count + 1, total: cur.total + minor });
  }
  return [...acc.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, v]) => ({ currency, count: v.count, totalMinor: v.total.toString() }));
}

export function loadPayoutQueue(runtime: { readonly finance: FinanceApi }, market: string | undefined, status: PayoutStatus = 'REQUESTED'): Promise<Result<{ payouts: readonly PayoutRow[] }, ApiError>> {
  return runtime.finance.payoutQueue({ ...(market !== undefined && market !== '' ? { market } : {}), status, limit: 100 });
}

export function decidePayout(runtime: { readonly finance: FinanceApi }, payoutId: string, input: PayoutDecisionInput): Promise<Result<PayoutRow & { decision: PayoutDecision }, ApiError>> {
  return runtime.finance.decidePayout(payoutId, input);
}
